/* eslint-disable jsx-a11y/role-supports-aria-props */

// TODO: Type in middle of text, flash of incorrect autocomplete bold phrases
//       (previous screen should freeze completely, looks like it gets an update?)
//
// TODO: aria attributes

import React, { Children, useEffect, useRef, useContext, useMemo } from "react";
import { string } from "prop-types";
import Portal from "@reach/portal";
import { wrapEvent } from "@reach/utils";
import useRect from "./useRect";
import useMachine, { useMachineWithLogger } from "./useMachine";
import { findAll } from "highlight-words-core";
import escapeRegexp from "escape-regexp";

let stateChart = {
  initial: "idle",
  idle: {
    CLOSE: "idle",
    CLEAR: "idle",
    CHANGE: "suggesting",
    NAVIGATE: "navigating"
  },
  suggesting: {
    CHANGE: "suggesting",
    NAVIGATE: "navigating",
    MOUSE_DOWN: "selectingWithClick",
    CLEAR: "idle",
    CLOSE: "idle"
  },
  navigating: {
    CHANGE: "suggesting",
    ARROW_UP_DOWN: "navigating",
    MOUSE_DOWN: "selectingWithClick",
    CLEAR: "idle",
    CLOSE: "idle",
    NAVIGATE: "navigating",
    SELECT_WITH_KEYBOARD: "idle"
  },
  selectingWithClick: {
    SELECT_WITH_CLICK: "idle"
  }
};

function reducer(data, action) {
  switch (action.type) {
    case "CHANGE":
      return {
        ...data,
        navigationValue: null,
        value: action.value
      };
    case "NAVIGATE":
      return {
        ...data,
        navigationValue: action.value
      };
    case "CLEAR":
      return {
        ...data,
        value: "",
        navigationValue: null
      };
    case "CLOSE":
      return {
        ...data,
        navigationValue: null
      };
    case "SELECT_WITH_CLICK":
      return {
        ...data,
        value: action.value,
        navigationValue: null
      };
    case "SELECT_WITH_KEYBOARD":
      return {
        ...data,
        value: data.navigationValue,
        navigationValue: null
      };
    default:
      return data;
  }
}

let visibleStates = ["suggesting", "navigating", "selectingWithClick"];
let selectingWithClickNode = null;

let Context = React.createContext();

export function Combobox({ children, onSelect }) {
  // We store the values of all the Options on this ref through context on
  // render.  This makes it possible to perform the keyboard navigation from
  // the input on the list.
  let optionsRef = useRef([]);

  // To position the menu over the input with useRect we need to store the
  // input ref up here.
  let inputRef = useRef(null);

  let defaultData = {
    // the value the user has typed
    value: "",

    // the value the user has navigated to with the keyboard
    navigationValue: null
  };

  let [state, data, transition] = useMachineWithLogger(
    stateChart,
    reducer,
    defaultData
  );

  let shouldMeasure = visibleStates.includes(state);
  let rect = useRect(inputRef, shouldMeasure);

  // TODO: useMemo on this
  let context = {
    data,
    inputRef,
    onSelect,
    optionsRef,
    rect,
    state,
    transition
  };

  return <Context.Provider value={context}>{children}</Context.Provider>;
}

export function ComboboxInput({
  // highlights all the text in the box on click when true
  selectOnClick = false,
  autocompleteOnNav = true,

  // wrapped events
  onClick,
  onChange,
  onKeyDown,
  onBlur,
  onFocus,

  ...props
}) {
  let {
    data: { navigationValue, value },
    inputRef,
    onSelect,
    optionsRef,
    state,
    transition
  } = useContext(Context);

  // Because we close the List on blur, we need to track if the blur is
  // caused by clicking inside the list, and if so, don't close the List.
  let selectOnClickRef = useRef(false);

  function handleBlur(event) {
    if (state !== "selectingWithClick") {
      transition("CLOSE");
    }
  }

  function handleChange(event) {
    let value = event.target.value;
    if (value.trim() === "") {
      transition("CLEAR");
    } else {
      transition("CHANGE", { value });
    }
  }

  function handleKeyDown(event) {
    let { current: options } = optionsRef;
    switch (event.key) {
      case "ArrowDown": {
        // Don't scroll the page
        event.preventDefault();

        // If the developer didn't render any options, there's no point in
        // trying to navigate
        if (!options || options.length === 0) {
          return;
        }

        if (state === "idle") {
          // Opening a closed list, we don't want to select anything,
          // just open it.
          transition("NAVIGATE", { value: null });
        } else {
          let index = options.indexOf(navigationValue);
          let atBottom = index === options.length - 1;
          if (atBottom) {
            // Go back to the value the user has typed
            transition("NAVIGATE", { value: null });
          } else {
            // Go to the next item in the list
            let nextValue = options[(index + 1) % options.length];
            transition("NAVIGATE", { value: nextValue });
          }
        }
        break;
      }
      case "ArrowUp": {
        // Don't scroll the page
        event.preventDefault();

        // If the developer didn't render any options, there's no point in
        // trying to navigate
        if (!options || options.length === 0) {
          return;
        }

        if (state === "idle") {
          transition("NAVIGATE", { value: null });
        } else {
          let index = options.indexOf(navigationValue);
          if (index === 0) {
            // Go back to the value the user has typed
            transition("NAVIGATE", { value: null });
          } else if (index === -1) {
            // select the last one
            let value = options.length ? options[options.length - 1] : null;
            transition("NAVIGATE", { value });
          } else {
            // normal case, select previous
            let nextValue =
              options[(index - 1 + options.length) % options.length];
            transition("NAVIGATE", { value: nextValue });
          }
        }
        break;
      }
      case "Escape": {
        if (state !== "idle") {
          transition("CLOSE");
        }
        break;
      }
      case "Enter": {
        if (state === "navigating" && navigationValue !== null) {
          // don't want to submit forms
          event.preventDefault();
          onSelect && onSelect(navigationValue);
          // not sure if we want this, if we don't do it, apps can do in
          // `onSelect` what they do in `onChange`, but probably want to keep
          // the old results after a selection
          // onChange && onChange(event);
          transition("SELECT_WITH_KEYBOARD");
        }
        break;
      }
      default: {
      }
    }
  }

  function handleFocus() {
    if (selectOnClick) {
      selectOnClickRef.current = true;
    }
  }

  function handleClick() {
    if (selectOnClickRef.current) {
      selectOnClickRef.current = false;
      inputRef.current.select();
    }
  }

  let inputValue =
    autocompleteOnNav && (state === "navigating" || "selectingWithClick")
      ? // When idle, we don't have a navigationValue on ArrowUp/Down
        navigationValue || value
      : value;

  return (
    <input
      ref={inputRef}
      {...props}
      value={inputValue}
      onClick={wrapEvent(onClick, handleClick)}
      onBlur={wrapEvent(onBlur, handleBlur)}
      onFocus={wrapEvent(onFocus, handleFocus)}
      onChange={wrapEvent(onChange, handleChange)}
      onKeyDown={wrapEvent(onKeyDown, handleKeyDown)}
    />
  );
}

export function ComboboxList({ children, ariaText, style, ...props }) {
  let {
    state,
    transition,
    optionsRef,
    onSelect,
    data: { navigationValue, value },
    rect
  } = useContext(Context);

  useEffect(() => {
    let handler = event => {
      if (selectingWithClickNode && selectingWithClickNode !== event.target) {
        selectingWithClickNode = null;
        transition("CLOSE");
      }
    };
    document.addEventListener("mouseup", handler);
    return () => document.removeEventListener("mouseup", handler);
  }, []);

  useEffect(() => {
    optionsRef.current = Children.map(
      children,
      ({ props: { value } }) => value
    );
    return () => (optionsRef.current = null);
  });

  let clones = Children.map(children, child =>
    React.cloneElement(child, {
      onSelect,
      contextValue: value,
      navigationValue,
      transition
    })
  );

  let el =
    visibleStates.includes(state) && rect ? (
      <ul
        style={{
          ...style,
          position: "fixed",
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        }}
        {...props}
        data-reach-combobox-list
        children={clones}
      />
    ) : null;
  return <Portal>{rect ? el : null}</Portal>;
}

ComboboxList.propTypes = {
  ariaText: string.isRequired
};

let OptionContext = React.createContext();

export function ComboboxOption({
  children,
  value,

  // implicit
  transition,
  onSelect,
  contextValue,
  navigationValue,

  // wrapped
  onClick,
  onMouseDown,

  ...props
}) {
  let isActive = navigationValue === value;

  function handleMouseDown(event) {
    selectingWithClickNode = event.target;
    transition("MOUSE_DOWN");
  }

  function handleClick() {
    transition("SELECT_WITH_CLICK", { value });
    onSelect(value);
  }

  return (
    <OptionContext.Provider value={value}>
      <li
        {...props}
        data-reach-combobox-option
        aria-selected={isActive}
        onClick={wrapEvent(onClick, handleClick)}
        onMouseDown={wrapEvent(onMouseDown, handleMouseDown)}
        children={children || <ComboboxOptionText />}
      />
    </OptionContext.Provider>
  );
}

export function ComboboxOptionText() {
  let value = useContext(OptionContext);
  let {
    data: { value: contextValue }
  } = useContext(Context);

  let searchWords = escapeRegexp(contextValue).split(/\s+/);
  let textToHighlight = value;
  let results = useMemo(() => findAll({ searchWords, textToHighlight }), [
    searchWords,
    textToHighlight
  ]);
  return results.length
    ? results.map((result, index) => {
        let str = value.slice(result.start, result.end);
        return (
          <span
            key={index}
            data-user-value={result.highlight ? true : undefined}
            data-suggested-value={result.highlight ? undefined : true}
          >
            {str}
          </span>
        );
      })
    : value;
}



// WEBPACK FOOTER //
// ./src/Combobox2.js